import { Col, Divider, Row, Select, Space, Spin, Typography } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import ChartCard from '../component/home/ChartCard';
import DueMaturityScheduleModal from '../component/home/DueMaturityScheduleModal';
import NoScheduleCardModal from '../component/home/NoScheduleCardModal';
import StatCard from '../component/home/StatCard';
import WrongPaidMaturityScheduleModal from '../component/home/WrongPaidMaturityScheduleModal';
import WrongPaymentMaturityScheduleModal from '../component/home/WrongPaymentMaturityScheduleModal';
import WrongPaymentWithdrawalServiceModal from '../component/home/WrongPaymentWithdrawalServiceModal';
import creditCardService from '../service/credit-card-service/Credit-card.service';
import maturityScheduleService from '../service/maturity-schedule-service/MaturitySchedule.service';
import ProfitReportService from '../service/profit-report-service/ProfitReport.service';
import TransactionService from '../service/transaction-service/Transaction.service';
import EmployeeCheck from '../component/home/EmployeeCheck';
import useRole from '../hook/useRole';
import useEmployee from '../hook/useEmployee';

const { Title } = Typography;

const Home3: React.FC = () => {
  const now = moment();
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(now);
  const [selectedMonth, setSelectedMonth] = useState<number>(now.month() + 1);
  const [selectedYear, setSelectedYear] = useState<number>(now.year());

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear - 10 + i);

  // State cho thẻ chưa có lịch đáo
  const [noScheduleCards, setNoScheduleCards] = useState<any[]>([]);
  const [loadingNoSchedule, setLoadingNoSchedule] = useState(false);
  const [showNoScheduleModal, setShowNoScheduleModal] = useState(false);

  // State cho thẻ Lịch đến hạn chưa nạp
  const [dueSchedules, setDueSchedules] = useState<any[]>([]);
  const [loadingDueSchedules, setLoadingDueSchedules] = useState(false);
  const [showDueModal, setShowDueModal] = useState(false);

  // State cho thẻ Lịch làm sai (nạp/rút)
  const [wrongPaidSchedules, setWrongPaidSchedules] = useState<any[]>([]);
  const [loadingWrongPaid, setLoadingWrongPaid] = useState(false);
  const [showWrongPaidModal, setShowWrongPaidModal] = useState(false);

  // State cho thẻ Lịch thanh toán sai (≥5K)
  const [wrongPaymentSchedules, setWrongPaymentSchedules] = useState<any[]>([]);
  const [loadingWrongPayment, setLoadingWrongPayment] = useState(false);
  const [showWrongPaymentModal, setShowWrongPaymentModal] = useState(false);

  // State cho thẻ DV rút tiền - Trả sai (≥51K)
  const [
    wrongPaymentWithdrawalTransactions,
    setWrongPaymentWithdrawalTransactions,
  ] = useState<any[]>([]);
  const [loadingWrongPaymentWithdrawal, setLoadingWrongPaymentWithdrawal] =
    useState(false);
  const [showWrongPaymentWithdrawalModal, setShowWrongPaymentWithdrawalModal] =
    useState(false);

  // State cho biểu đồ Tổng doanh thu - chi phí - lợi nhuận theo tháng
  const [profitChartData, setProfitChartData] = useState<any[]>([]);
  const [loadingProfitChart, setLoadingProfitChart] = useState(false);

  const role = useRole();
  const { employee } = useEmployee();

  // State cho lịch làm sai và thanh toán sai đã lọc theo employee
  const [filteredWrongPaidSchedules, setFilteredWrongPaidSchedules] = useState<
    any[]
  >([]);
  const [filteredWrongPaymentSchedules, setFilteredWrongPaymentSchedules] =
    useState<any[]>([]);
  const [
    filteredWrongPaymentWithdrawalTransactions,
    setFilteredWrongPaymentWithdrawalTransactions,
  ] = useState<any[]>([]);

  // Function để lọc chỉ những kỳ đáo hạn có chênh lệch thanh toán >= 5000 VND
  const filterByPaymentThreshold = (schedules: any[]) => {
    return schedules.filter((schedule: any) => {
      const totalWithdrawal = schedule.totalWithdrawalAmount || 0;
      const totalDeposit = schedule.totalDepositAmount || 0;
      const fee = schedule.feeApplied || 0;
      const chenhLech = Math.abs(totalDeposit - totalWithdrawal);
      const thanhTien = totalWithdrawal * (fee / 100) + chenhLech;
      const totalPayment = schedule.totalPaymentAmount || 0;
      const paymentDifference = thanhTien - totalPayment; // Giữ nguyên dấu

      // Chỉ giữ lại nếu thiếu >= 5000 VND hoặc thừa >= 5000 VND
      const isUnderPaid = paymentDifference >= 5000; // Thiếu >= 5000
      const isOverPaid = paymentDifference <= -5000; // Thừa >= 5000

      return isUnderPaid || isOverPaid;
    });
  };

  const fetchDataForMonth = (date: moment.Moment) => {
    const month = date.month() + 1;
    const year = date.year();
    console.log(`Fetching data for ${month}/${year}`);
    // Gọi API ở đây
  };

  useEffect(() => {
    const newDate = moment(`${selectedYear}-${selectedMonth}`, 'YYYY-MM');
    setSelectedDate(newDate);
    fetchDataForMonth(newDate);
  }, [selectedMonth, selectedYear]);

  // Gọi API lấy thẻ chưa có lịch đáo
  useEffect(() => {
    setLoadingNoSchedule(true);
    creditCardService
      .getCreditCardsWithoutMaturityScheduleInMonth(selectedMonth, selectedYear)
      .then((data) => setNoScheduleCards(data))
      .catch(() => setNoScheduleCards([]))
      .finally(() => setLoadingNoSchedule(false));
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    setLoadingDueSchedules(true);
    maturityScheduleService
      .getDueOrOverdueToday()
      .then(setDueSchedules)
      .catch(() => setDueSchedules([]))
      .finally(() => setLoadingDueSchedules(false));
  }, []);

  useEffect(() => {
    setLoadingWrongPaid(true);
    maturityScheduleService
      .getWrongPaidSchedulesByMonthAndYear(selectedMonth, selectedYear)
      .then((data) => {
        setWrongPaidSchedules(data);
        if (role === 'ROLE_EMPLOYEE' && employee?.id) {
          setFilteredWrongPaidSchedules(
            data.filter(
              (item) =>
                Array.isArray(item.employees) &&
                item.employees.some(
                  (emp: { id: number }) => emp.id === employee.id
                )
            )
          );
        } else {
          setFilteredWrongPaidSchedules(data);
        }
      })
      .catch(() => {
        setWrongPaidSchedules([]);
        setFilteredWrongPaidSchedules([]);
      })
      .finally(() => setLoadingWrongPaid(false));
  }, [selectedMonth, selectedYear, role, employee?.id]);

  useEffect(() => {
    setLoadingWrongPayment(true);
    maturityScheduleService
      .getWrongPaymentSchedulesByMonthAndYear(selectedMonth, selectedYear)
      .then((data) => {
        // Filter theo ngưỡng 5000 VND trước
        const filteredByThreshold = filterByPaymentThreshold(data);

        setWrongPaymentSchedules(filteredByThreshold);
        if (role === 'ROLE_EMPLOYEE' && employee?.id) {
          setFilteredWrongPaymentSchedules(
            filteredByThreshold.filter(
              (item) =>
                Array.isArray(item.employees) &&
                item.employees.some(
                  (emp: { id: number }) => emp.id === employee.id
                )
            )
          );
        } else {
          setFilteredWrongPaymentSchedules(filteredByThreshold);
        }
      })
      .catch(() => {
        setWrongPaymentSchedules([]);
        setFilteredWrongPaymentSchedules([]);
      })
      .finally(() => setLoadingWrongPayment(false));
  }, [selectedMonth, selectedYear, role, employee?.id]);

  // Fetch wrong payment withdrawal service transactions
  useEffect(() => {
    setLoadingWrongPaymentWithdrawal(true);
    TransactionService.getWrongPaymentWithdrawalServiceTransactions(
      selectedMonth,
      selectedYear
    )
      .then((data) => {
        setWrongPaymentWithdrawalTransactions(data);
        if (role === 'ROLE_EMPLOYEE' && employee?.id) {
          setFilteredWrongPaymentWithdrawalTransactions(
            data.filter(
              (item) => item.transaction?.employee?.id === employee.id
            )
          );
        } else {
          setFilteredWrongPaymentWithdrawalTransactions(data);
        }
      })
      .catch(() => {
        setWrongPaymentWithdrawalTransactions([]);
        setFilteredWrongPaymentWithdrawalTransactions([]);
      })
      .finally(() => setLoadingWrongPaymentWithdrawal(false));
  }, [selectedMonth, selectedYear, role, employee?.id]);

  useEffect(() => {
    if (role === 'ROLE_EMPLOYEE') {
      return;
    }
    setLoadingProfitChart(true);
    ProfitReportService.getYearlyProfitSummary(selectedYear)
      .then((res) => {
        if (!res?.months?.length) {
          setProfitChartData([]);
          return;
        }
        // Đảm bảo có đủ 12 tháng, điền 0 nếu thiếu
        const monthsArr = Array.from({ length: 12 }, (_, i) => {
          const found = res.months.find((m) => m.month === i + 1);
          return (
            found || {
              month: i + 1,
              totalRevenue: 0,
              totalExpense: 0,
              netProfit: 0,
            }
          );
        });
        setProfitChartData(monthsArr);

        // Hiển thị thông báo nếu có dữ liệu
        const hasData = monthsArr.some(
          (month) =>
            month.totalRevenue > 0 ||
            month.totalExpense > 0 ||
            month.netProfit > 0
        );
        if (hasData) {
          console.log('✅ Biểu đồ doanh thu đã được tải thành công');
        }
      })
      .catch((error) => {
        console.error('❌ Lỗi khi tải dữ liệu biểu đồ:', error);
        setProfitChartData([]);
      })
      .finally(() => setLoadingProfitChart(false));
  }, [selectedYear, role]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}>Tổng quan</Title>
        <Space>
          <Select
            value={selectedMonth}
            onChange={setSelectedMonth}
            style={{ width: 100 }}
            placeholder="Tháng"
          >
            {months.map((m) => (
              <Select.Option key={m} value={m}>
                Tháng {m}
              </Select.Option>
            ))}
          </Select>

          <Select
            value={selectedYear}
            onChange={setSelectedYear}
            style={{ width: 100 }}
            placeholder="Năm"
          >
            {years.map((y) => (
              <Select.Option key={y} value={y}>
                {y}
              </Select.Option>
            ))}
          </Select>
        </Space>
      </div>

      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Thẻ chưa có lịch đáo"
            value={loadingNoSchedule ? <Spin /> : noScheduleCards.length}
            onClick={() => setShowNoScheduleModal(true)}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Lịch đến hạn chưa nạp"
            value={loadingDueSchedules ? <Spin /> : dueSchedules.length}
            onClick={() => setShowDueModal(true)}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Lịch làm sai (nạp/rút)"
            value={
              loadingWrongPaid ? <Spin /> : filteredWrongPaidSchedules.length
            }
            onClick={() => setShowWrongPaidModal(true)}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Lịch thanh toán sai (≥5K)"
            value={
              loadingWrongPayment ? (
                <Spin />
              ) : (
                filteredWrongPaymentSchedules.length
              )
            }
            onClick={() => setShowWrongPaymentModal(true)}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="DV rút tiền - Trả sai (≥51K)"
            value={
              loadingWrongPaymentWithdrawal ? (
                <Spin />
              ) : (
                filteredWrongPaymentWithdrawalTransactions.length
              )
            }
            onClick={() => setShowWrongPaymentWithdrawalModal(true)}
          />
        </Col>
      </Row>

      {/* Nếu KHÔNG phải employee thì mới hiển thị biểu đồ và EmployeeCheck */}
      {role !== 'ROLE_EMPLOYEE' && (
        <>
          <Row gutter={[16, 16]} className="mb-6">
            <Col span={24}>
              <ChartCard
                title="Tổng quan doanh thu - chi phí - lợi nhuận theo tháng"
                data={profitChartData}
                loading={loadingProfitChart}
              />
            </Col>
          </Row>

          <Divider orientation="center" style={{ borderColor: '#7cb305' }} />

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <EmployeeCheck />
            </Col>
          </Row>
        </>
      )}

      {/* Modal hiển thị danh sách thẻ chưa có lịch đáo */}
      <NoScheduleCardModal
        open={showNoScheduleModal}
        onClose={() => setShowNoScheduleModal(false)}
        loading={loadingNoSchedule}
        cards={noScheduleCards}
      />

      <DueMaturityScheduleModal
        open={showDueModal}
        onClose={() => setShowDueModal(false)}
        loading={loadingDueSchedules}
        schedules={dueSchedules}
      />

      <WrongPaidMaturityScheduleModal
        open={showWrongPaidModal}
        onClose={() => setShowWrongPaidModal(false)}
        loading={loadingWrongPaid}
        schedules={filteredWrongPaidSchedules}
        role={role}
      />

      <WrongPaymentMaturityScheduleModal
        open={showWrongPaymentModal}
        onClose={() => setShowWrongPaymentModal(false)}
        loading={loadingWrongPayment}
        schedules={filteredWrongPaymentSchedules}
        role={role}
      />

      <WrongPaymentWithdrawalServiceModal
        open={showWrongPaymentWithdrawalModal}
        onClose={() => setShowWrongPaymentWithdrawalModal(false)}
        loading={loadingWrongPaymentWithdrawal}
        transactions={filteredWrongPaymentWithdrawalTransactions}
        role={role}
      />
    </div>
  );
};

export default Home3;
